
import { useState, useEffect } from 'react';

export default function Projects() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('projects');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleProjectClick = (githubUrl: string) => {
    if (!githubUrl || githubUrl === '#') return;
    window.open(githubUrl, '_blank', 'noopener,noreferrer');
  };

  const projects = [
    {
      id: 1,
      title: 'RDBMS with Golang',
      description:
        'A relational database management system implemented in Go, focusing on practical query execution, indexing, and storage layout. Built to deeply understand how databases work under the hood while keeping the API ergonomic for real-world usage.',
      tech: ['Golang', 'RDBMS Internals', 'Query Engine', 'Indexes', 'Transactions'],
      github: 'https://github.com/hel-mefe/rdbms-go',
      demo: '#',
      featured: true
    },
    {
      id: 2,
      title: 'Nginx-Similar HTTP Server',
      description:
        'An HTTP/1.1 web server written in C++ inspired by Nginx, with configurable virtual hosts, custom configuration language, and support for multiplexing with kqueue/epoll. Designed as a production-style educational project to deeply learn networking, HTTP, and performance trade-offs.',
      tech: ['C++', 'HTTP/1.1', 'Sockets', 'Epoll/Kqueue', 'Config Parser'],
      github: 'https://github.com/hel-mefe/Nginx-similar-webserver-42',
      demo: '#',
      featured: true
    },
    {
      id: 3,
      title: 'Shell Command Interpreter',
      description:
        'A Unix-style shell implemented in C that parses and executes commands, manages processes, and recreates key shell features. Built to get hands-on with parsing, signals, process control, and low-level Unix APIs.',
      tech: ['C', 'Unix', 'Shell', 'Parsing', 'Processes'],
      github: 'https://github.com/hel-mefe/minishell-42',
      demo: '#',
      featured: false
    },
    {
      id: 4,
      title: 'Extream Pong – Real-Time Game',
      description:
        'A real-time multiplayer pong game with a Django backend handling matchmaking, game state, and real-time communication. Focused on latency, synchronization, and delivering a smooth gameplay experience over the network.',
      tech: ['Django', 'WebSockets', 'Real-time Systems', 'Game Logic'],
      github: 'https://github.com/hel-mefe/ExtreamPong',
      demo: '#',
      featured: false
    },
    {
      id: 5,
      title: 'FDF – 2D/3D Wireframe Engine',
      description:
        'A graphics project that renders 2D/3D wireframe models from scratch using the MiniLibX library. Implements line-drawing algorithms, projections, rotations, Z-scaling, and color handling to display maps as interactive wireframes.',
      tech: ['C', 'Graphics', 'Wireframe Rendering', 'Linear Algebra'],
      github: 'https://github.com/hel-mefe/FDF-42',
      demo: '#',
      featured: false
    }
  ];

  return (
    <section id="projects" className="py-20 px-4 relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full animate-pulse" style={{
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          animationDuration: '6s'
        }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`}>
          <div className="inline-block bg-gray-900/80 border border-green-500/30 rounded-lg p-4 mb-8 backdrop-blur-md hover-glow">
            <div className="text-green-400 text-left">
              <span className="text-green-500 animate-pulse">$</span> ls -la projects/
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 gradient-text">Projects</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto mb-8 animate-pulse"></div>
          <p className="text-gray-300 max-w-2xl mx-auto hover:text-green-300 transition-colors duration-300">
            A collection of my work spanning from low-level C/C++ systems to high-level web architectures and AI-powered products,
            showcasing a tech-agnostic mindset and a focus on building robust, well-designed solutions end to end.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project.github)}
              className={`group bg-gray-900/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm hover:border-green-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-green-500/10 hover-scale cursor-pointer ${
                project.featured ? 'ring-2 ring-green-500/40 shadow-lg shadow-green-500/20 bg-gray-900/70' : ''
              } ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${0.3 + index * 0.1}s` }}
            >
              {/* Featured Badge */}
              {project.featured && (
                <div className="inline-block bg-gradient-to-r from-green-500/30 to-emerald-500/30 text-green-300 text-xs px-3 py-1 rounded-full border border-green-500/50 mb-4 animate-glow shadow-md shadow-green-500/30">
                  <i className="ri-star-fill mr-1 text-green-400"></i>
                  Featured
                </div>
              )}

              {/* Project Header */}
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-white group-hover:text-green-400 transition-colors duration-300 gradient-text flex-1 pr-4">
                  {project.title}
                </h3>
                <div className="flex space-x-3">
                  <a
                    href={project.github}
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-400 hover:text-green-400 transition-colors duration-300 cursor-pointer hover-scale"
                    title="View Source"
                  >
                    <i className="ri-github-line text-xl"></i>
                  </a>
                  <a
                    href={project.demo}
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-400 hover:text-green-400 transition-colors duration-300 cursor-pointer hover-scale"
                    title="Live Demo"
                  >
                    <i className="ri-external-link-line text-xl"></i>
                  </a>
                </div>
              </div>

              {/* Project Description */}
              <p className="text-gray-300 text-sm mb-6 leading-relaxed group-hover:text-green-200 transition-colors duration-300">
                {project.description}
              </p>
              
              {/* Tech Stack */}
              <div className="flex flex-wrap gap-2">
                {project.tech.map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-xs border border-green-500/20 hover:bg-green-500/20 hover-scale cursor-pointer animate-float transition-all duration-300"
                    style={{ animationDelay: `${techIndex * 0.1}s` }}
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Terminal-style footer */}
              <div className="mt-6 pt-4 border-t border-green-500/20">
                <div className="text-green-400 text-xs opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-green-500">$</span> git clone {project.title.toLowerCase().replace(/\s+/g, '-')}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View More */}
        <div className={`text-center mt-12 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`} style={{ animationDelay: '1.5s' }}>
          <button className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-8 py-3 rounded-lg border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale animate-float">
            <i className="ri-github-line mr-2 animate-pulse"></i>
            View More on GitHub
          </button>
        </div>
      </div>
    </section>
  );
}
